import {
  AUTHENTICATION_V2_ENDPOINT,
  SUBSCRIPTION_SERVICE_ENDPOINT
} from '@dtx-company/true-common/src/constants/endpoints'
import { Cookie } from '@dtx-company/true-common/src/utils/cookies'
import { Domains } from '@dtx-company/true-common/src/types/domains'
import {
  FronteggObserver,
  IthacaJwt,
  SignUpLocation,
  buildJwtFromFronteggAccessToken,
  clearCache,
  signOut,
  subscribeFronteggObserver,
  unsubscribeFronteggObserver
} from '@dtx-company/ithaca-sdk/src'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { clearSSORefreshTokenFromLocalStorage } from '@dtx-company/true-common/src/utils/localStorage/clearSSORefreshTokenFromLocalStorage'
import {
  clearUserData,
  setCurrentUserData,
  setImpersonatedUserData
} from '../../redux/slices/user-slice'
import { flowcodeApi } from '../../redux/slices/flowcodeApiSlice/init'
import { flowpageApi } from '../../redux/slices/flowpageApiSlice/empty'
import { getPlans } from '@dtx-company/inter-app/src/api/subscription-service/getPlans'
import { handleDatadogLogger, logger } from '@dtx-company/logger'
import { printStoreApi } from '../../redux/slices/printStoreApiSlice/init'
import { setDemo } from '../../redux/slices/current-session-slice'
import { useAPISWR } from '../useAPISWR/useAPISWR'
import { useCallback, useEffect, useState } from 'react'
import { useCookies } from '@dtx-company/true-common/src/utils/cookies/cookies.hooks'
import { useDispatch } from 'react-redux'
import { usePlanPricingGroup } from '../usePlanPricingGroup/usePlanPricingGroup'
import { useRefreshTokenBeforeExpiration } from './useRefreshTokenBeforeExpiration'
import { useRestoreSession } from './useRestoreSession'
import { useReverseTrialState } from '../useReverseTrialState'
import { useRouter } from 'next/router'
import { useSWRConfig } from 'swr'
import Cookies from 'universal-cookie'
import events from '@dtx-company/inter-app/src/event-tracking/events/account'

const cookies = new Cookies()

function useFronteggHandleImpersonationAccessToken(): (
  impersonationAccessToken: string
) => Promise<void> {
  const dispatch = useDispatch()
  const onSignout = useOnSignout()
  return useCallback(
    async impersonationAccessToken => {
      try {
        const jwt = await buildJwtFromFronteggAccessToken(impersonationAccessToken)
        if (jwt) {
          dispatch(setImpersonatedUserData({ data: jwt }))
          dispatch(flowpageApi.util.resetApiState())
          dispatch(flowcodeApi.util.resetApiState())
          dispatch(printStoreApi.util.resetApiState())
        }
      } catch (e) {
        logger.logError(e, { technicalArea: 'authentication' })
        onSignout()
        return
      }
    },
    [dispatch, onSignout]
  )
}

const getTypedDomain = (_domain: string | undefined): Domains => {
  return _domain === 'flowpage' ? Domains.FLOWPAGE : Domains.FLOWCODE
}

function useStartProTrial(): (email: string) => Promise<boolean> {
  const group = usePlanPricingGroup()
  const { data } = useAPISWR(
    getPlans,
    () => {
      return { filter: { groupEq: group } }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: true
    }
  )
  const [plans, setPlans] = useState<Plan[] | undefined>(undefined)

  //data becomes undefined intermittently, so we store the last defined value
  useEffect(() => {
    if (data) {
      setPlans(data)
    }
  }, [data])

  const proPlan = plans
    ?.filter(plan => plan.type === PlanType.PRO && plan.interval === PlanInterval.MONTHLY)
    ?.pop()

  const reverseTrialState = useReverseTrialState()
  const reverseTrialEnabled = reverseTrialState.enabled

  const startProTrial = useCallback(
    async (email: string) => {
      if (reverseTrialEnabled && proPlan) {
        try {
          const nowInMilliseconds = Date.now()
          const millisecondsInAnHour = 60 * 60 * 1000
          const oneHourInTheFuture = new Date(nowInMilliseconds + millisecondsInAnHour)
          const res = await fetch(`${SUBSCRIPTION_SERVICE_ENDPOINT}/subscriptions`, {
            method: 'POST',
            body: JSON.stringify({
              email: email,
              planId: proPlan.id,
              trialEndAt: oneHourInTheFuture.toISOString() //this needs to be non-null future ISO string to match backend Go types, but backend ignores this value and sets end date independently
            })
          })

          if (res.ok) {
            return true
          } else {
            await logger.logFetchError(res, {
              team: 'monetization',
              technicalArea: 'authentication'
            })
            return false
          }
        } catch (e) {
          logger.logError(e, { team: 'monetization', technicalArea: 'authentication' })
          return false
        }
      }

      if (reverseTrialEnabled && !proPlan) {
        logger.error({
          name: 'Pro plan fetch trial error',
          message: 'Reverse trial enabled but no pro plan found',
          team: 'creatorjourney',
          technicalArea: 'authentication'
        })
      }

      return false
    },
    [reverseTrialEnabled, proPlan]
  )

  return startProTrial
}

async function recordReferral(friendId: string, referrerId: string): Promise<void> {
  try {
    const res = await fetch(`${AUTHENTICATION_V2_ENDPOINT}/referrals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        referrerId,
        friendId
      })
    })
    if (!res.ok) {
      await logger.logFetchError(res, { team: 'monetization', technicalArea: 'authentication' })
    }
  } catch (e) {
    logger.logError(e, { team: 'monetization', technicalArea: 'authentication' })
  }
}

function checkIfUserJustSignedUp(jwt: IthacaJwt): boolean {
  const fiveMinutes = 5 * 60 * 1000
  const nowInMilliseconds = Date.now()
  const userCreatedAtTimeInMilliseconds = new Date(jwt?.createdAt ?? 0).getTime()
  const timeSinceUserCreated = nowInMilliseconds - userCreatedAtTimeInMilliseconds
  return Boolean(jwt?.createdAt && timeSinceUserCreated < fiveMinutes)
}

function recordSignup(
  jwt: IthacaJwt,
  domain: string | undefined,
  signUpLocation?: SignUpLocation
): void {
  events.userCreatedAccount(jwt?.email || '', getTypedDomain(domain), signUpLocation)
}

function useRecordReferral(): (newUserId: string) => Promise<void> {
  const router = useRouter()
  const referrerId = router.query.referrerId as string | undefined
  return useCallback(
    async newUserId => {
      if (referrerId) {
        await recordReferral(newUserId, referrerId)
      }
    },
    [referrerId]
  )
}

export function useHandleSignupSideEffects(): (
  newUserId: IthacaJwt,
  domain: string | undefined,
  signUpLocation?: SignUpLocation
) => Promise<{ didStartProTrial: boolean }> {
  const recordReferral = useRecordReferral()
  const startProTrial = useStartProTrial()
  const router = useRouter()
  const referrerId = router.query.referrerId as string | undefined
  return useCallback(
    async (newUserJwt, domain, signUpLocation) => {
      recordSignup(newUserJwt, domain, signUpLocation)
      if (referrerId) {
        await recordReferral(newUserJwt.ithacaId)
      }
      const didStartProTrial = await startProTrial(newUserJwt.ithacaId)
      return { didStartProTrial }
    },
    [referrerId, recordReferral, startProTrial]
  )
}

export function useHandleFronteggAccessToken(): (
  accessToken: string,
  domain?: string,
  location?: SignUpLocation
) => Promise<void> {
  const dispatch = useDispatch()
  const onSignout = useOnSignout()
  const handleSignupSideEffects = useHandleSignupSideEffects()
  return useCallback(
    async (accessToken, domain, location) => {
      try {
        let jwt = await buildJwtFromFronteggAccessToken(accessToken)
        let justSignedUp = false

        if (!jwt) {
          throw new Error('handleFronteggAccessToken - JWT failed to build')
        } else {
          justSignedUp = checkIfUserJustSignedUp(jwt)
          if (justSignedUp) {
            const { didStartProTrial } = await handleSignupSideEffects(jwt, domain, location)
            if (didStartProTrial) {
              const updatedJwt = await buildJwtFromFronteggAccessToken(accessToken)
              jwt = updatedJwt ?? jwt
              if (!updatedJwt) {
                throw new Error('Failed to update JWT after signing new user up for pro trial')
              }
            }
          }
          dispatch(setCurrentUserData({ data: jwt, isNewUser: justSignedUp, isFronteggUser: true }))
          dispatch(setDemo({ demo: false }))
          handleDatadogLogger(jwt.ithacaId)
        }
      } catch (e) {
        logger.logError(e, { technicalArea: 'authentication' })
        onSignout()
      }
    },
    [dispatch, handleSignupSideEffects, onSignout]
  )
}

export function useClearAuthState(): () => void {
  const onSignout = useOnSignout()
  const dispatch = useDispatch()

  return useCallback(() => {
    dispatch(clearUserData())
    onSignout()
  }, [onSignout, dispatch])
}

export function useListenForAuthChanges(): void {
  useRestoreSession()
  useRefreshTokenBeforeExpiration()
  useListenForFronteggAuthChanges()
}

export function useListenForFronteggAuthChanges(): void {
  const { optOut } = useCookies()

  const clearAuthState = useClearAuthState()
  const handleFronteggAccessToken = useHandleFronteggAccessToken()
  const handleFronteggImpersonationAccessToken = useFronteggHandleImpersonationAccessToken()
  useEffect(() => {
    const observer: FronteggObserver = {
      onLogin: handleFronteggAccessToken,
      onSignup: handleFronteggAccessToken,
      onRefresh: handleFronteggAccessToken,
      onLogout: clearAuthState,
      onImpersonation: handleFronteggImpersonationAccessToken
    }

    subscribeFronteggObserver(observer)

    return () => {
      unsubscribeFronteggObserver(observer)
    }
    //include optOut in dependency array to make sure we refresh the user when their cookies change
  }, [handleFronteggAccessToken, handleFronteggImpersonationAccessToken, optOut, clearAuthState])
}

function useOnSignout(): () => void {
  const { mutate } = useSWRConfig()

  return useCallback(async () => {
    clearCache()

    mutate(() => true, undefined, { revalidate: false })

    clearSSORefreshTokenFromLocalStorage()

    cookies.remove(Cookie.token, { path: '/' })

    await signOut()
  }, [mutate])
}
